/**
 * Read Page Component
 * Full Quran Reader with immersive card carousel UI
 *
 * Features:
 * - Mushaf-style page reading (1-604 pages)
 * - Card carousel with smooth swipe navigation (Embla Carousel)
 * - Two modes: Read (pure mushaf) and Tadabbur (verse-by-verse with tafsir)
 * - Immersive full-screen mode by default (tap to show/hide controls)
 * - Background prefetching for instant page transitions
 * - Audio player bar
 * - Settings (font size, reciter, tajweed)
 * - Save progress on exit
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { BookOpen, Heart } from 'lucide-react';
import { useTheme } from '@/components/ui/theme-provider';
import QuranPageView from './QuranPageView';
import QuranControls from './QuranControls';
import AudioPlayerBar from './AudioPlayerBar';
import SettingsDrawer from './SettingsDrawer';
import SaveProgressDrawer from './SaveProgressDrawer';
import ChapterInfoDrawer from './ChapterInfoDrawer';
import TafsirDrawer from './TafsirDrawer';
import TajweedRulesDrawer from './TajweedRulesDrawer';
import RandomVerseDialog from './RandomVerseDialog';
import { LoadingScreen } from '@/components/ui/loading-screen';
import type { Ayah, ReaderSettings, SurahListItem, Reciter } from '@/types/quran';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import {
  getFirstPageOfSurah,
  saveReadingProgress,
  getReaderSettings,
  saveReaderSettings,
  getVerseAudio,
  getPreviewAudioUrl,
  getSurahs,
  getReciters,
} from '@/services/quran.service';
import { useQuranPageCache } from '@/hooks/useQuranPageCache';
import { sessionsService } from '@/services/sessions.service';
import type { ApiError } from '@/lib/axios';
import { cn } from '@/lib/utils';

const TOTAL_PAGES = 604;

type ReadMode = 'read' | 'tadabbur';

export function ReadPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { effectiveTheme } = useTheme();

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [savedPage, setSavedPage] = useState<number | null>(null);
  const [settings, setSettings] = useState<ReaderSettings>(getReaderSettings());
  const [surahs, setSurahs] = useState<SurahListItem[]>([]);
  const [reciters, setReciters] = useState<Reciter[]>([]);
  const [selectedSurahNumber, setSelectedSurahNumber] = useState<number | undefined>(undefined);
  const [readMode, setReadMode] = useState<ReadMode>('read');

  // Immersive mode — UI hidden by default
  const [showUI, setShowUI] = useState(false);

  // UI State
  const [showSettings, setShowSettings] = useState(false);
  const [showSaveProgress, setShowSaveProgress] = useState(false);
  const [saveProgressLoading, setSaveProgressLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  // New feature state
  const [showChapterInfo, setShowChapterInfo] = useState(false);
  const [showTafsir, setShowTafsir] = useState(false);
  const [showTajweedRules, setShowTajweedRules] = useState(false);
  const [showRandomVerse, setShowRandomVerse] = useState(false);
  const [tafsirTarget, setTafsirTarget] = useState<{ surah: number; ayah: number }>({ surah: 1, ayah: 1 });
  const [chapterInfoSurah, setChapterInfoSurah] = useState<SurahListItem | null>(null);

  // Audio
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playQueueRef = useRef<string[]>([]);
  const queueIndexRef = useRef(0);
  const [audioActive, setAudioActive] = useState(false);

  // Tadabbur: play single ayah
  const tadabburAudioRef = useRef<HTMLAudioElement | null>(null);
  const [playingAyahKey, setPlayingAyahKey] = useState<string | null>(null);
  const [loadingAyahKey, setLoadingAyahKey] = useState<string | null>(null);

  // Carousel API
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  // Page cache hook
  const { getPageData, initialLoading, prefetchAround } = useQuranPageCache(currentPage, {
    readMode,
    settings,
  });

  // Get ayahs for the 3 carousel slides: [prev, current, next]
  const prevPage = currentPage > 1 ? currentPage - 1 : TOTAL_PAGES;
  const nextPage = currentPage < TOTAL_PAGES ? currentPage + 1 : 1;

  const prevAyahs = useMemo(() => getPageData(prevPage), [getPageData, prevPage]);
  const currentAyahs = useMemo(() => getPageData(currentPage), [getPageData, currentPage]);
  const nextAyahs = useMemo(() => getPageData(nextPage), [getPageData, nextPage]);

  // Update selected surah from current page data
  useEffect(() => {
    if (currentAyahs.length > 0 && currentAyahs[0].surahNumber) {
      setSelectedSurahNumber(currentAyahs[0].surahNumber);
    }
  }, [currentAyahs]);

  // Load surahs + reciters list on mount
  useEffect(() => {
    getSurahs().then(setSurahs).catch(console.error);
    getReciters().then(setReciters).catch(console.error);
  }, []);

  // Load initial page
  useEffect(() => {
    loadInitialPage();
  }, []);

  const loadInitialPage = async () => {
    try {
      const res = await sessionsService.list({ limit: 1 });
      const first = res?.data?.[0];
      const page = first?.end_page;
      if (page != null && page >= 1 && page <= TOTAL_PAGES) {
        setCurrentPage(page);
        setSavedPage(page);
      } else {
        setCurrentPage(1);
      }
    } catch {
      setCurrentPage(1);
    }
  };

  // Save settings when they change
  useEffect(() => {
    saveReaderSettings(settings);
  }, [settings]);

  // ─── Page Navigation ─────────────────────────────────────

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage >= TOTAL_PAGES) {
      setCurrentPage(1);
    } else {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage]);

  // Carousel slide change → update current page
  // We use a "settling" mechanism: the carousel always starts on slide index 1 (middle).
  // When user swipes to slide 0 → previous page, slide 2 → next page.
  const isSettlingRef = useRef(false);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      if (isSettlingRef.current) return;

      const selected = carouselApi.selectedScrollSnap();
      if (selected === 0) {
        // Swiped right (RTL) → Slide 0 = nextPage → go forward in Quran
        isSettlingRef.current = true;
        handleNextPage();
      } else if (selected === 2) {
        // Swiped left (RTL) → Slide 2 = prevPage → go backward in Quran
        isSettlingRef.current = true;
        handlePreviousPage();
      }
    };

    carouselApi.on('select', onSelect);
    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi, handleNextPage, handlePreviousPage]);

  // Re-center carousel whenever currentPage changes
  useEffect(() => {
    if (!carouselApi) return;

    // Reset scroll position on all slides to top
    carouselApi.slideNodes().forEach((node) => {
      node.scrollTop = 0;
    });

    // Jump back to middle slide (index 1) without animation
    isSettlingRef.current = true;
    carouselApi.scrollTo(1, true); // true = jump (no animation)
    // Small delay before re-enabling swipe detection
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isSettlingRef.current = false;
      });
    });
  }, [currentPage, carouselApi]);

  // ─── Surah Selection ─────────────────────────────────────

  const handleSelectSurah = async (surahNumber: number) => {
    try {
      const surahInfo = surahs.find(s => s.number === surahNumber);
      const page = await getFirstPageOfSurah(surahNumber);
      setCurrentPage(page);
      setSelectedSurahNumber(surahNumber);

      if (surahInfo) {
        toast.success(`ไปยัง ${surahInfo.englishName} (${surahInfo.englishNameTranslation})`);
      } else {
        toast.success(`ไปยังซูเราห์ที่ ${surahNumber}`);
      }

      // Prefetch around the new page
      prefetchAround(page);
    } catch (error) {
      console.error('Error loading surah:', error);
      toast.error('ไม่สามารถโหลดสูเราะห์นี้ได้');
    }
  };

  // ─── Bookmark & Save ─────────────────────────────────────

  const handleBookmark = () => {
    setShowSaveProgress(true);
  };

  const handleClose = () => {
    setShowSaveProgress(true);
  };

  const handleSaveProgress = async () => {
    saveReadingProgress(currentPage);
    setSaveProgressLoading(true);
    try {
      const result = await sessionsService.create({ end_page: currentPage });
      if (result.goal_completed && result.completed_goal_id) {
        setShowSaveProgress(false);
        navigate(`/goal-completed/${result.completed_goal_id}`, { replace: true });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
      await queryClient.invalidateQueries({
        queryKey: ['goals', 'current', 'progress'],
      });
      toast.success('บันทึกความคืบหน้าแล้ว');
      navigate(-1);
    } catch (error) {
      console.error('Session API error:', error);
      const apiErr = error as ApiError;
      if (apiErr.code === 'VALIDATION_ERROR') {
        toast.error(
          'ยังบันทึกไม่ได้ — กรุณาอ่านรอบก่อนหน้าให้ครบก่อนขึ้นเล่มใหม่ หรือบันทึกผ่านเมนูเซสชันแทน',
          { duration: 5000, position: 'top-center' },
        );
      } else {
        toast.error('บันทึกความคืบหน้าไม่สำเร็จ');
      }
    } finally {
      setSaveProgressLoading(false);
    }
  };

  const handleDiscardProgress = () => {
    navigate(-1);
  };

  const handleCancelSave = () => {
    setShowSaveProgress(false);
  };

  // ─── Settings ─────────────────────────────────────────────

  const handleSettingsChange = (newSettings: ReaderSettings) => {
    setSettings(newSettings);
  };

  const handleFontSizeChange = (size: ReaderSettings['fontSize']) => {
    setSettings(prev => ({ ...prev, fontSize: size }));
  };

  const handleGoToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(TOTAL_PAGES, page)));
  };

  // ─── Tafsir & Chapter Info ────────────────────────────────

  const handleAyahTap = (surahNumber: number, ayahNumber: number) => {
    setTafsirTarget({ surah: surahNumber, ayah: ayahNumber });
    setShowTafsir(true);
  };

  const handleSurahHeaderTap = (surahNumber: number) => {
    const surah = surahs.find((s) => s.number === surahNumber) ?? null;
    setChapterInfoSurah(surah);
    setShowChapterInfo(true);
  };

  const handleShareAyah = useCallback((ayah: Ayah) => {
    navigate(`/share/ayah/${ayah.surahNumber}/${ayah.numberInSurah}`, {
      state: {
        ayah,
        meaningLanguage: 'th',
        showArabic: true,
      },
    });
  }, [navigate]);

  // ─── Audio ────────────────────────────────────────────────

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    playQueueRef.current = [];
    queueIndexRef.current = 0;
    setIsPlaying(false);
    setAudioActive(false);
  }, []);

  const playNextInQueue = useCallback(() => {
    const queue = playQueueRef.current;
    const idx = queueIndexRef.current;
    if (idx >= queue.length) {
      setIsPlaying(false);
      return;
    }
    const url = queue[idx];
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => {
      queueIndexRef.current += 1;
      playNextInQueue();
    };
    audio.onerror = () => {
      toast.error('โหลดเสียงไม่สำเร็จ');
      queueIndexRef.current += 1;
      playNextInQueue();
    };
    audio.play().catch(() => {
      toast.error('เล่นเสียงไม่สำเร็จ');
      queueIndexRef.current += 1;
      playNextInQueue();
    });
  }, []);

  const handleTogglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audioActive) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play().catch(() => {});
        setIsPlaying(true);
      }
      return;
    }
    handleStartAudio();
  }, [isPlaying, audioActive]);

  const handleStartAudio = useCallback(async () => {
    const reciterId = settings.selectedReciter ?? '7';
    const withSurah = currentAyahs.filter(
      (a): a is Ayah & { surahNumber: number } =>
        a.surahNumber != null && a.surahNumber > 0
    );
    if (withSurah.length === 0) {
      toast.error('ไม่มีข้อมูลเสียงสำหรับหน้านี้');
      return;
    }

    setIsAudioLoading(true);
    try {
      const urls: string[] = [];
      for (const ayah of withSurah) {
        const url = await getVerseAudio(ayah.surahNumber, ayah.numberInSurah, reciterId);
        if (url) urls.push(url);
      }
      if (urls.length === 0) {
        toast.error('ไม่พบเสียงของกอรีที่เลือก');
        return;
      }
      playQueueRef.current = urls;
      queueIndexRef.current = 0;
      setAudioActive(true);
      setIsPlaying(true);
      playNextInQueue();
    } catch (err) {
      console.error('Audio load error:', err);
      toast.error('โหลดเสียงไม่สำเร็จ');
    } finally {
      setIsAudioLoading(false);
    }
  }, [currentAyahs, settings.selectedReciter, playNextInQueue]);

  // Restart audio automatically when reciter changes while playing
  const audioActiveRef = useRef(false);
  audioActiveRef.current = audioActive;
  useEffect(() => {
    if (!audioActiveRef.current) return;
    // Stop current playback and restart with new reciter
    const reciterId = settings.selectedReciter ?? '7';
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    playQueueRef.current = [];
    queueIndexRef.current = 0;
    setIsPlaying(false);
    // Short delay then restart with the new reciter URL
    const timer = setTimeout(async () => {
      const withSurah = currentAyahs.filter(
        (a): a is Ayah & { surahNumber: number } =>
          a.surahNumber != null && a.surahNumber > 0
      );
      if (withSurah.length === 0) return;
      setIsAudioLoading(true);
      try {
        const urls: string[] = [];
        for (const ayah of withSurah) {
          const url = await getVerseAudio(ayah.surahNumber, ayah.numberInSurah, reciterId);
          if (url) urls.push(url);
        }
        if (urls.length === 0) { toast.error('ไม่พบเสียงของกอรีที่เลือก'); return; }
        playQueueRef.current = urls;
        queueIndexRef.current = 0;
        setAudioActive(true);
        setIsPlaying(true);
        playNextInQueue();
      } catch { toast.error('โหลดเสียงไม่สำเร็จ'); }
      finally { setIsAudioLoading(false); }
    }, 100);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.selectedReciter]);

  const handleSkipBack = useCallback(() => {
    if (queueIndexRef.current <= 0) return;
    if (audioRef.current) audioRef.current.pause();
    queueIndexRef.current -= 1;
    setIsPlaying(true);
    playNextInQueue();
  }, [playNextInQueue]);

  const handleSkipForward = useCallback(() => {
    const queue = playQueueRef.current;
    if (queueIndexRef.current >= queue.length - 1) return;
    if (audioRef.current) audioRef.current.pause();
    queueIndexRef.current += 1;
    setIsPlaying(true);
    playNextInQueue();
  }, [playNextInQueue]);

  const stopTadabburAudio = useCallback(() => {
    if (tadabburAudioRef.current) {
      tadabburAudioRef.current.pause();
      tadabburAudioRef.current = null;
    }
    setPlayingAyahKey(null);
    setLoadingAyahKey(null);
  }, []);

  const handlePlayAyah = useCallback(
    async (surahNumber: number, ayahNumber: number, reciterIdOverride?: string) => {
      const key = `${surahNumber}:${ayahNumber}`;
      if (playingAyahKey === key && tadabburAudioRef.current) {
        tadabburAudioRef.current.pause();
        setPlayingAyahKey(null);
        return;
      }
      if (tadabburAudioRef.current) {
        tadabburAudioRef.current.pause();
        tadabburAudioRef.current = null;
      }
      const reciterId = reciterIdOverride ?? settings.selectedReciter ?? '7';
      setLoadingAyahKey(key);
      try {
        const url = await getVerseAudio(surahNumber, ayahNumber, reciterId);
        if (!url) {
          toast.error('ไม่พบเสียงอายะฮ์นี้');
          return;
        }
        const audio = new Audio(url);
        tadabburAudioRef.current = audio;
        audio.onended = () => {
          setPlayingAyahKey(null);
          tadabburAudioRef.current = null;
        };
        audio.onerror = () => {
          toast.error('เล่นเสียงไม่สำเร็จ');
          setPlayingAyahKey(null);
          tadabburAudioRef.current = null;
        };
        await audio.play();
        setPlayingAyahKey(key);
      } catch (err) {
        console.error('Tadabbur audio error:', err);
        toast.error('เล่นเสียงไม่สำเร็จ');
        setPlayingAyahKey(null);
      } finally {
        setLoadingAyahKey(null);
      }
    },
    [playingAyahKey, settings.selectedReciter]
  );

  /** เล่นเสียงตัวอย่างกอรี (อายะห์ 1:1 หรือต้นอัลฟาติฮะห์) — ใช้เมื่อเลือกกอรีใน Settings */
  const handlePlayReciterPreview = useCallback(async (reciterId: string) => {
    if (tadabburAudioRef.current) {
      tadabburAudioRef.current.pause();
      tadabburAudioRef.current = null;
    }
    setPlayingAyahKey(null);
    setLoadingAyahKey('1:1');
    try {
      const url = await getPreviewAudioUrl(reciterId);
      if (!url) {
        toast.error('กอรีนี้ไม่มีเสียงตัวอย่าง');
        return;
      }
      const audio = new Audio(url);
      tadabburAudioRef.current = audio;
      audio.onended = () => {
        setPlayingAyahKey(null);
        tadabburAudioRef.current = null;
      };
      audio.onerror = () => {
        toast.error('เล่นเสียงไม่สำเร็จ');
        setPlayingAyahKey(null);
        tadabburAudioRef.current = null;
      };
      await audio.play();
      setPlayingAyahKey('1:1');
    } catch (err) {
      console.error('Reciter preview audio error:', err);
      toast.error('เล่นเสียงไม่สำเร็จ');
      setPlayingAyahKey(null);
    } finally {
      setLoadingAyahKey(null);
    }
  }, []);

  // Stop audio when leaving page or when page changes
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  useEffect(() => {
    stopAudio();
    stopTadabburAudio();
  }, [currentPage, stopAudio, stopTadabburAudio]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (showSettings || showSaveProgress) return;

      switch (e.key) {
        case 'ArrowLeft':
          // RTL: Left is Next page (higher page number)
          handleNextPage();
          break;
        case 'ArrowRight':
          // RTL: Right is Previous page (lower page number)
          handlePreviousPage();
          break;
        case 'Home':
          setCurrentPage(1);
          break;
        case 'End':
          setCurrentPage(TOTAL_PAGES);
          break;
        case 'Escape':
          setShowUI((prev) => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, showSettings, showSaveProgress, handleNextPage, handlePreviousPage]);

  // ─── Toggle immersive mode on tap ─────────────────────────
  const lastTapRef = useRef(0);
  const handleContentTap = useCallback(() => {
    const now = Date.now();
    // Debounce rapid taps
    if (now - lastTapRef.current < 300) return;
    lastTapRef.current = now;
    setShowUI((prev) => !prev);
  }, []);

  // Compute juz/hizb from first ayah
  const firstAyah = currentAyahs[0];
  const currentJuz = firstAyah?.juz ?? 1;
  const currentSurahNumber = firstAyah?.surahNumber;
  const displaySurahNumber = selectedSurahNumber ?? currentSurahNumber;

  // ─── Render helper for a single slide ─────────────────────
  const renderSlide = useCallback(
    (ayahs: Ayah[], pageNum: number) => (
      <QuranPageView
        ayahs={ayahs}
        currentPage={pageNum}
        showTranslation={settings.showTranslation}
        showTajweed={settings.showTajweed}
        fontSize={settings.fontSize}
        surahs={surahs}
        readMode={readMode}
        theme={effectiveTheme}
        onAyahTap={handleAyahTap}
        onSurahHeaderTap={handleSurahHeaderTap}
        onPlayAyah={readMode === 'tadabbur' ? handlePlayAyah : undefined}
        onShareAyah={readMode === 'tadabbur' ? handleShareAyah : undefined}
        playingAyahKey={readMode === 'tadabbur' ? playingAyahKey : null}
        loadingAyahKey={readMode === 'tadabbur' ? loadingAyahKey : null}
      />
    ),
    [settings, surahs, readMode, effectiveTheme, handlePlayAyah, handleShareAyah, playingAyahKey, loadingAyahKey],
  );

  if (initialLoading && currentAyahs.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="fixed inset-0 z-40 bg-background">
      {/* Top Controls — hidden by default (immersive mode) */}
      <QuranControls
        currentPage={currentPage}
        totalPages={TOTAL_PAGES}
        savedPage={savedPage}
        juz={currentJuz}
        currentSurahNumber={displaySurahNumber}
        surahs={surahs}
        onSelectSurah={handleSelectSurah}
        onOpenSettings={() => setShowSettings(true)}
        onBookmark={handleBookmark}
        onClose={handleClose}
        onGoToPage={handleGoToPage}
        fontSize={settings.fontSize}
        onFontSizeChange={handleFontSizeChange}
        reciters={reciters}
        selectedReciterId={settings.selectedReciter}
        onReciterChange={(id) => setSettings(prev => ({ ...prev, selectedReciter: id }))}
        visible={showUI}
      />

      {/* Mode Tabs — Read vs Tadabbur (hidden in immersive mode) */}
      <div
        className={cn(
          'fixed top-[5.5rem] left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50',
          'transition-all duration-300 ease-in-out',
          !showUI && '-translate-y-[200%] opacity-0 pointer-events-none',
        )}
      >
        <div className="max-w-3xl mx-auto flex">
          <button
            onClick={() => setReadMode('read')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
              readMode === 'read'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <BookOpen className="w-4 h-4" />
            อ่าน
          </button>
          <button
            onClick={() => setReadMode('tadabbur')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
              readMode === 'tadabbur'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Heart className="w-4 h-4" />
            ตะดับบุร
          </button>
        </div>
      </div>

      {/* Subtle page indicator — visible when UI is hidden */}
      <div
        className={cn(
          'fixed top-0 left-0 right-0 z-40 flex items-center justify-center py-2',
          'transition-all duration-300 ease-in-out',
          showUI && 'opacity-0 pointer-events-none',
        )}
      >
        <span className="text-xs text-muted-foreground/90 bg-background/60 backdrop-blur-md px-3 py-1.5 rounded-full shadow-sm border border-border/20 flex items-center gap-2">
          {displaySurahNumber && (
            <>
              <span className="font-medium">
                ซูเราะห์ที่ {displaySurahNumber} {surahs.find(s => s.number === displaySurahNumber)?.englishName}
              </span>
              <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
            </>
          )}
          <span>หน้า {currentPage} / {TOTAL_PAGES}</span>
        </span>
      </div>

      {/* Carousel Content — tap to toggle UI */}
      <div
        className="h-full w-full"
        onClick={handleContentTap}
      >
        <Carousel
          opts={{
            align: 'center',
            loop: false,
            startIndex: 1,
            watchDrag: true,
            duration: 20,
          }}
          setApi={setCarouselApi}
          className="h-full w-full"
        >
          <CarouselContent className="h-full ml-0">
            {/* Slide 0: Next page in Quran (RTL swipe-right reveals this) */}
            <CarouselItem className="h-full pl-0 overflow-y-auto" style={{ paddingTop: showUI ? '8.5rem' : '2rem', paddingBottom: (audioActive || isAudioLoading) ? '8rem' : '2rem' }}>
              {renderSlide(nextAyahs, nextPage)}
            </CarouselItem>

            {/* Slide 1: Current page (always centered) */}
            <CarouselItem className="h-full pl-0 overflow-y-auto" style={{ paddingTop: showUI ? '8.5rem' : '2rem', paddingBottom: (audioActive || isAudioLoading) ? '8rem' : '2rem' }}>
              {renderSlide(currentAyahs, currentPage)}
            </CarouselItem>

            {/* Slide 2: Previous page in Quran (RTL swipe-left reveals this) */}
            <CarouselItem className="h-full pl-0 overflow-y-auto" style={{ paddingTop: showUI ? '8.5rem' : '2rem', paddingBottom: (audioActive || isAudioLoading) ? '8rem' : '2rem' }}>
              {renderSlide(prevAyahs, prevPage)}
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>

      {/* Bottom Audio Player */}
      {(audioActive || isAudioLoading) && (
        <AudioPlayerBar
          audioRef={audioRef}
          isPlaying={isPlaying}
          isAudioLoading={isAudioLoading}
          onTogglePlay={handleTogglePlay}
          onStop={stopAudio}
          onSkipBack={handleSkipBack}
          onSkipForward={handleSkipForward}
          reciters={reciters}
          selectedReciterId={settings.selectedReciter}
          onReciterChange={(id) => setSettings(prev => ({ ...prev, selectedReciter: id }))}
        />
      )}

      {/* Floating audio button — visible only when UI is shown */}
      {!audioActive && !isAudioLoading && showUI && (
        <div className="fixed bottom-8 right-6 z-50 flex flex-col gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleStartAudio();
            }}
            className="flex items-center gap-2 px-3 py-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 transition-opacity text-sm font-medium"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </button>
        </div>
      )}

      {/* Settings Drawer */}
      <SettingsDrawer
        open={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSettingsChange={handleSettingsChange}
        onReciterSelect={handlePlayReciterPreview}
        previewAudioLoading={loadingAyahKey === '1:1'}
      />

      {/* Save Progress Dialog */}
      <SaveProgressDrawer
        open={showSaveProgress}
        currentPage={currentPage}
        startPage={savedPage ?? 0}
        onSave={handleSaveProgress}
        onDiscard={handleDiscardProgress}
        onCancel={handleCancelSave}
        isSaving={saveProgressLoading}
      />

      {/* Chapter Info Drawer */}
      <ChapterInfoDrawer
        open={showChapterInfo}
        onClose={() => setShowChapterInfo(false)}
        surah={chapterInfoSurah}
      />

      {/* Tafsir Drawer */}
      <TafsirDrawer
        open={showTafsir}
        onClose={() => setShowTafsir(false)}
        surahNumber={tafsirTarget.surah}
        ayahNumber={tafsirTarget.ayah}
        selectedTafsirId={settings.selectedTranslation}
        selectedReciterId={settings.selectedReciter}
        translationSource={settings.tadabburTranslationSource === 'quranenc' ? 'enc' : 'qf'}
      />

      {/* Tajweed Rules Drawer with Floating Button (only when UI visible) */}
      <TajweedRulesDrawer
        open={showTajweedRules}
        onClose={() => setShowTajweedRules(false)}
        onToggle={showUI ? () => setShowTajweedRules(!showTajweedRules) : undefined}
        isEnabled={settings.showTajweed ?? false}
      />

      {/* Random Verse Dialog */}
      <RandomVerseDialog
        open={showRandomVerse}
        onClose={() => setShowRandomVerse(false)}
        translationId={settings.selectedTranslation}
      />
    </div>
  );
}

export default ReadPage;
